/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  BuilderPermission,
  PreConstructionScheduleRestService,
  SystemRoleBit,
  systemRolesBitSet,
  UserRestService,
} from '@gh/builder-shared-data';
import { InitViewService, UserContext } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { MessageBasket } from '@gh/core-messages';
import { DialogService, WaitingMode, WaitingService } from '@gh/core-ui';
import { expandObservable } from '@gh/core-util';
import { of$, throwError$ } from '@gh/rx';
import { map$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  worksheetTemplateDialogRoute,
} from '../components/pre-construction-worksheet/worksheet-template-dialog/worksheet-template-dialog.component';
import {
  PreConstructionWorksheetDetails,
  preConstructionWorksheetDetailsFromDto,
  preConstructionWorksheetDetailsToSaveDto,
  PreConstructionWorksheetJobAdmin,
} from './pre-construction-worksheet.model';
import { omit } from 'lodash';

@Injectable()
export class PreConstructionWorksheetBusinessService implements InitViewService {
  queryParams: any;
  constructor(private userRest: UserRestService,
              private scheduleRest: PreConstructionScheduleRestService,
              private userContext: UserContext,
              private waitings: WaitingService,
              private dialogs: DialogService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    this.queryParams = omit(route.queryParams, 'parentRoute');
    return this.getNominatedTemplate().pipe(
      this.waitings.operator(WaitingMode.Background),
      switchMap$((nominatedTemplateId) => nominatedTemplateId
        ? [nominatedTemplateId]
        : this.nominateTemplate()),
      switchMap$((templateId) => templateId ? of$(templateId) : throwError$(
        'No default Pre-Construction template assigned')),
      switchMap$((templateId) =>
        expandObservable({
          details: this.init(templateId),
        }).pipe((this.waitings.operator(WaitingMode.Background)))));
  }

  getNominatedTemplate(): Observable<ID> {
    return this.scheduleRest.initWorksheet().pipe(map$(({ nominatedTemplateId }) => nominatedTemplateId));
  }

  init(templateId: ID): Observable<PreConstructionWorksheetDetails> {
    return this.scheduleRest.getWorksheet(templateId, this.queryParams).pipe(
      map$((details) => preConstructionWorksheetDetailsFromDto(details, templateId)));
  }

  save(details: PreConstructionWorksheetDetails): Observable<PreConstructionWorksheetDetails> {
    return this.scheduleRest.saveWorksheet(preConstructionWorksheetDetailsToSaveDto(details)).pipe(
      switchMap$(() => this.init(details.templateId)));
  }

  findAllJobAdministrators(query: FilteredDataQuery<string>): Observable<PreConstructionWorksheetJobAdmin[]> {
    return this.userRest.findAllAsDataSource({
      autocomplete: true,
      name: query.filter,
      showInactive: false,
      systemRoles: systemRolesBitSet(SystemRoleBit.JobAdmin),
    }).select(query).pipe(map$(getSlicedData));
  }

  private nominateTemplate(): Observable<ID> {
    if (!this.userContext.hasPermission(BuilderPermission.Schedules.PreConstruction.CREATE)
      && !this.userContext.hasPermission(BuilderPermission.Schedules.PreConstruction.EDIT)) {
      return throwError$(MessageBasket.error('msg_sch_no_worksheet'));
    }

    return this.dialogs.openWithInput(worksheetTemplateDialogRoute, {})
      .afterClosed();
  }
}
